import { useThree } from '@react-three/fiber'
import { usePointCloudContext } from 'features/pointClouds'
import { usePointDetection } from 'pages/pointClouds/components/observations/hooks/usePointDetection'
import { PointMetadata } from 'pages/pointClouds/utils/pointMetadataExtractor'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Vector3 } from 'three'

export interface HoveredPointData {
  position: Vector3
  pointMetadata: PointMetadata | null
  screenPosition: { x: number; y: number }
}

interface UsePointHoverReturn {
  hoveredPoint: HoveredPointData | null
  mousePosition: { x: number; y: number }
  isHovering: boolean
}

export function usePointHover(
  enabled: boolean,
  debounceMs: number = 150
): UsePointHoverReturn {
  const { gl } = useThree()
  const { enableTooltips } = usePointCloudContext()

  const shouldShowTooltips = enableTooltips

  const [hoveredPoint, setHoveredPoint] = useState<HoveredPointData | null>(
    null
  )
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  })

  const { detectPoints } = usePointDetection(enabled && shouldShowTooltips)
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isHoveringRef = useRef(false)
  const lastDetectionTime = useRef<number>(0)

  const debouncedDetectPoints = useCallback(() => {
    if (!enabled || !shouldShowTooltips) {
      setHoveredPoint(null)
      return
    }

    const detection = detectPoints()

    if (detection.bestHit && detection.bestHit.pointMetadata) {
      const newHoveredPoint: HoveredPointData = {
        position: detection.bestHit.point,
        pointMetadata: detection.bestHit.pointMetadata,
        screenPosition: mousePosition,
      }
      setHoveredPoint(newHoveredPoint)
      isHoveringRef.current = true
    } else {
      setHoveredPoint(null)
      isHoveringRef.current = false
    }
  }, [detectPoints, enabled, shouldShowTooltips, mousePosition])

  useEffect(() => {
    const canvas = gl.domElement

    const handleMouseMove = (event: MouseEvent) => {
      const newMousePosition = {
        x: event.clientX,
        y: event.clientY,
      }

      setMousePosition(newMousePosition)

      const now = Date.now()
      if (now - lastDetectionTime.current >= debounceMs) {
        lastDetectionTime.current = now
        debouncedDetectPoints()
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current)
        }
        throttleTimeoutRef.current = setTimeout(() => {
          lastDetectionTime.current = Date.now()
          debouncedDetectPoints()
        }, debounceMs)
      }
    }

    const handleMouseLeave = () => {
      setHoveredPoint(null)
      isHoveringRef.current = false
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }
    }

    if (enabled && shouldShowTooltips) {
      canvas.addEventListener('mousemove', handleMouseMove)
      canvas.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseleave', handleMouseLeave)
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }
    }
  }, [
    gl.domElement,
    enabled,
    shouldShowTooltips,
    debounceMs,
    debouncedDetectPoints,
  ])

  // Clean up on disable
  useEffect(() => {
    if (!enabled || !shouldShowTooltips) {
      setHoveredPoint(null)
      isHoveringRef.current = false
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current)
      }
    }
  }, [enabled, shouldShowTooltips])

  return {
    hoveredPoint,
    mousePosition,
    isHovering: hoveredPoint !== null,
  }
}
